import { useEffect, useState } from 'react';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Gap kept between a bar lifted above the keyboard and the keyboard itself. It is the same distance
 * the conversation footer normally sits above the bottom of the screen on Android (see
 * `.conversationFooter` in Inbox.module.scss), so a bar riding the keyboard looks the same as at rest.
 */
export const KEYBOARD_GAP_PX = 14;

/**
 * Height in px of the on-screen keyboard (0 when it is closed).
 *
 * The native app runs the Capacitor keyboard in `resize: none` mode, so the screen is NOT resized
 * when the keyboard opens and anything pinned to the bottom (position: fixed) ends up behind it.
 * Elements that need to ride above the keyboard read this and lift themselves — the same approach
 * the search bars in BookingsListModal / ContactsListModal use.
 *
 * Listens to the Capacitor keyboard events, with a visualViewport fallback for when they don't fire
 * (the fallback reads 0 when the browser already resizes the page for the keyboard, so nothing is
 * lifted twice).
 */
export function useKeyboardHeight(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const apply = (h: number) => {
      const next = h > 0 ? Math.round(h) : 0;
      setHeight((prev) => (prev === next ? prev : next));
    };

    const handles: Array<Promise<{ remove: () => Promise<void> }>> = [];
    try {
      handles.push(Keyboard.addListener('keyboardWillShow', (info) => apply(info.keyboardHeight || 0)));
      handles.push(Keyboard.addListener('keyboardDidShow', (info) => apply(info.keyboardHeight || 0)));
      handles.push(Keyboard.addListener('keyboardWillHide', () => apply(0)));
      handles.push(Keyboard.addListener('keyboardDidHide', () => apply(0)));
    } catch { /* not available (web) */ }
    // Swallow listener-registration failures on platforms without the plugin
    handles.forEach((p) => p.catch(() => { }));

    const vv = window.visualViewport;
    let raf = 0;
    const onViewportResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!vv) return;
        const offset = window.innerHeight - vv.height - vv.offsetTop;
        apply(offset > 50 ? offset : 0);
      });
    };
    if (vv) vv.addEventListener('resize', onViewportResize);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (vv) vv.removeEventListener('resize', onViewportResize);
      handles.forEach((p) => { p.then((h) => h.remove()).catch(() => { }); });
    };
  }, []);

  return height;
}
